import React from "react";
import { Tooltip, theme } from "antd";
import type { TFunction } from "i18next";
import { space } from "../../theme/space";
import { SectionLabel } from "../../components/surfaces";

/**
 * The small pieces the Analytics page is assembled from.
 *
 * They exist to keep one rule in one place: **a figure never appears without
 * what it means.** A number with no unit, a percentage with nothing to compare
 * against, an arrow whose direction is good in one row and bad in the next —
 * each of those is a dashboard asking the reader to supply the interpretation,
 * which is exactly the thing this page is supposed to do for them.
 */

/** Status palette. Paired with a glyph, never carrying meaning on their own. */
const GOOD = "#0ca30c";
const BAD = "#d03b3b";

/**
 * Hours as a person would say them.
 *
 * "62.4" is a measurement, not an answer; "2d 14h" is the answer. Past ten days
 * the hours are dropped — at that scale they are noise, and reading "11d 3h"
 * invites a precision the average does not have.
 */
export const formatDuration = (
  hours: number | null | undefined,
  t: TFunction
): string => {
  if (hours === null || hours === undefined) return "—";
  if (hours < 1) return t("Under an hour");
  if (hours < 24) return t("{{hours}}h", { hours: Math.round(hours) });
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours % 24);
  if (days >= 10 || rest === 0) return t("{{days}}d", { days });
  return t("{{days}}d {{hours}}h", { days, hours: rest });
};

/** Days of waiting, rounded to what the number can actually support. */
export const formatDays = (
  days: number | null | undefined,
  t: TFunction
): string => {
  if (days === null || days === undefined) return "—";
  if (days < 1) return t("under a day");
  if (days < 2) return t("1 day");
  return t("{{count}} days", { count: Math.round(days) });
};

/** "3 days ago" — enough for a list of things that just happened. */
export const formatAgo = (iso: string, t: TFunction): string => {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const hours = (Date.now() - then) / 3600000;
  if (hours < 1) return t("just now");
  if (hours < 24) return t("{{hours}}h ago", { hours: Math.round(hours) });
  const days = Math.round(hours / 24);
  if (days < 2) return t("yesterday");
  if (days < 30) return t("{{count}} days ago", { count: days });
  return new Date(iso).toLocaleDateString();
};

/**
 * The change against the previous period.
 *
 * `direction` says which way is good — and is deliberately left unset for
 * figures where nobody can say. More cases started is not good or bad news, it
 * is just news; painting it green because the line went up is the dashboard
 * inventing an opinion. Where a direction *is* meaningful the arrow carries it
 * as well as the colour, so the cue survives a colour-blind reader and a
 * black-and-white print.
 */
export const Trend: React.FC<{
  deltaPct: number | null;
  previous: number | null;
  /** Which way is good news. Omitted = report the change, judge nothing. */
  direction?: "up" | "down";
  /** What the comparison is against, spelled out on hover. */
  comparison: string;
  t: TFunction;
}> = ({ deltaPct, previous, direction, comparison, t }) => {
  const { token } = theme.useToken();

  if (deltaPct === null || deltaPct === undefined) {
    // No percentage is honest here — most often because the previous period was
    // zero, where "+100%" would be read as a rate. The raw comparison still is.
    return (
      <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: space.hair }}>
        {previous === null || previous === undefined
          ? t("No comparison available")
          : t("{{count}} in the period before", { count: previous })}
      </div>
    );
  }

  const rising = deltaPct > 0;
  const flat = Math.abs(deltaPct) < 0.05;
  const good = direction === undefined ? null : rising === (direction === "up");
  const color = flat
    ? token.colorTextTertiary
    : good === null
    ? token.colorTextSecondary
    : good
    ? GOOD
    : BAD;

  return (
    <Tooltip title={comparison}>
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: space.hair,
          marginTop: space.hair,
          fontSize: 12,
          color,
          fontVariantNumeric: "tabular-nums",
          cursor: "default",
        }}
      >
        <span aria-hidden style={{ fontSize: 10 }}>
          {flat ? "—" : rising ? "▲" : "▼"}
        </span>
        <span>
          {flat
            ? t("No change")
            : rising
            ? t("{{percent}}% more", { percent: Math.abs(deltaPct) })
            : t("{{percent}}% less", { percent: Math.abs(deltaPct) })}
        </span>
      </div>
    </Tooltip>
  );
};

/**
 * A panel's own heading row: what it shows, and optionally a key or a control.
 *
 * Deliberately not a `Typography.Title` — the page has one heading level, in
 * `PageHeader`, and a second h-tag per panel would give a screen reader six
 * competing outlines of one screen.
 */
export const PanelHeading: React.FC<{
  title: string;
  hint?: string;
  extra?: React.ReactNode;
}> = ({ title, hint, extra }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: space.base,
        flexWrap: "wrap",
        marginBottom: space.base,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: token.colorText }}>
          {title}
        </div>
        {hint && (
          <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 2 }}>
            {hint}
          </div>
        )}
      </div>
      {extra}
    </div>
  );
};

/** One swatch + name. The chart's key, next to the title it belongs to. */
export const SeriesKey: React.FC<{ color: string; label: string }> = ({
  color,
  label,
}) => {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: space.hair + 2,
        fontSize: 12,
        color: token.colorTextSecondary,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 10,
          height: 10,
          borderRadius: 3,
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
};

/**
 * What a panel says when it has nothing to draw.
 *
 * A chart of one flat line at zero looks broken; this says the same thing and
 * looks deliberate. The second line always tells the reader what would make the
 * panel fill up, so an empty dashboard still teaches the product.
 */
export const EmptyState: React.FC<{
  title: string;
  hint: string;
  height?: number;
}> = ({ title, hint, height = 160 }) => {
  const { token } = theme.useToken();
  return (
    <div
      style={{
        minHeight: height,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: space.hair,
        padding: space.wide,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500, color: token.colorTextSecondary }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: token.colorTextTertiary, maxWidth: 380 }}>
        {hint}
      </div>
    </div>
  );
};

/**
 * A row of a ranked list: a name, a bar for its share, and its numbers.
 *
 * The bar is a *comparison aid*, not the datum — the count is written beside it
 * in every row, so nothing has to be measured against the axis that is not
 * drawn. Bars are one colour for the same reason the report charts are: length
 * already encodes the value, and shading by it spends the only free channel
 * saying so twice.
 */
export const BarRow: React.FC<{
  label: React.ReactNode;
  sublabel?: React.ReactNode;
  /** 0–1. */
  share: number;
  color: string;
  value: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
  title?: string;
}> = ({ label, sublabel, share, color, value, meta, onClick, title }) => {
  const { token } = theme.useToken();
  const clickable = Boolean(onClick);
  return (
    <div
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={title}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!clickable) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick!();
        }
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.base,
        padding: `${space.snug}px ${space.tight}px`,
        margin: `0 -${space.tight}px`,
        borderRadius: token.borderRadius,
        cursor: clickable ? "pointer" : "default",
        transition: "background 120ms",
      }}
      onMouseEnter={(event) => {
        if (clickable) event.currentTarget.style.background = token.colorFillQuaternary;
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.background = "transparent";
      }}
    >
      <div style={{ flex: "1 1 auto", minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            color: token.colorText,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </div>
        {/* The bar sits under the name rather than in its own column: the names
            are process and state names, which are long, and a fixed bar column
            would truncate them to make room for decoration. */}
        <div
          style={{
            marginTop: 6,
            height: 6,
            borderRadius: 3,
            background: token.colorFillQuaternary,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.max(share * 100, share > 0 ? 2 : 0)}%`,
              height: "100%",
              background: color,
              borderRadius: 3,
            }}
          />
        </div>
        {sublabel && (
          <div style={{ marginTop: 6, fontSize: 12, color: token.colorTextTertiary }}>
            {sublabel}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flex: "0 0 auto" }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: token.colorText,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}
        </div>
        {meta && (
          <div style={{ fontSize: 12, color: token.colorTextTertiary, marginTop: 2 }}>
            {meta}
          </div>
        )}
      </div>
    </div>
  );
};

/** A quiet identifier chip — a process's short name beside a longer label. */
export const Chip: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = theme.useToken();
  return (
    <span
      style={{
        display: "inline-block",
        padding: "0 6px",
        borderRadius: 4,
        background: token.colorFillQuaternary,
        color: token.colorTextTertiary,
        fontSize: 11,
        lineHeight: "18px",
        verticalAlign: "middle",
      }}
    >
      {children}
    </span>
  );
};

export { SectionLabel };
